import { Slot } from "@radix-ui/react-slot";
import type { ComponentProps } from "react";

import { alternadorEmPilulaClass, opcaoDoAlternadorClass } from "../../lib/superficies";
import { cn } from "../../lib/utils";

/**
 * TUT-511 — o ALTERNADOR em pílula do Lumen (P34): o grupo de opções que
 * TROCA A LEITURA da tela. Mês | Semana | Dia da Agenda, as modalidades de
 * Viagens, Lista | Kanban, Simples | Detalhada, a densidade do quadro e o
 * tipo de envio do atendimento são este primitivo, e nenhuma tela monta a
 * pílula nem a opção escolhida à mão.
 *
 * Antes dele, cada tela escrevia `Button variant="quiet"` com um
 * `opcaoEscolhidaClass` por cima, e o resultado era uma fileira de botões
 * retangulares ao lado de uma busca e de filtros já em pílula (a varredura da
 * TUT-510 achou). O desenho é de duas receitas em `superficies.ts`
 * (`alternadorEmPilulaClass`, `opcaoDoAlternadorClass`), as mesmas do
 * `GrupoDeEscolha` `pilula` e do `SectionBar`.
 *
 * ## Qual usar
 *
 * - Botões que trocam um estado da tela (a visão da Agenda, o filtro de
 *   modalidade): `AlternadorEmPilula` + `OpcaoDoAlternador` (`aria-pressed`).
 * - Links entre telas irmãs (Lista | Kanban, que são rotas): `tipo="navegacao"`
 *   e `OpcaoDoAlternador asChild` em volta do `<Link>` (`aria-current="page"`).
 * - Abas com painel (Atendimento | Chat): o `SectionBar`; quando a aba troca
 *   o CONJUNTO de colunas de um quadro e quem monta o teclado é a tela (a
 *   Elaboração), `tipo="abas"` e `OpcaoDoAlternador role="tab"`
 *   (`aria-selected`).
 * - Escolha de um CAMPO de formulário: o `GrupoDeEscolha`.
 */
export function AlternadorEmPilula({
  rotulo,
  tipo = "grupo",
  className,
  ...props
}: Omit<ComponentProps<"div">, "aria-label" | "role"> & {
  /** O nome do grupo (leitor de tela): "Visualização", "Ver como"… */
  rotulo: string;
  /**
   * `grupo`: botões de estado. `navegacao`: links entre rotas (`<nav>`).
   * `abas`: `role="tablist"`, com as opções em `role="tab"`.
   */
  tipo?: "grupo" | "navegacao" | "abas";
}) {
  const Raiz = tipo === "navegacao" ? "nav" : "div";
  return (
    <Raiz
      data-slot="alternador-em-pilula"
      role={tipo === "grupo" ? "group" : tipo === "abas" ? "tablist" : undefined}
      aria-label={rotulo}
      className={cn(alternadorEmPilulaClass, className)}
      {...props}
    />
  );
}

export function OpcaoDoAlternador({
  escolhida,
  asChild = false,
  className,
  type,
  ...props
}: Omit<ComponentProps<"button">, "aria-pressed" | "aria-current" | "aria-selected"> & {
  /** A opção que está valendo agora. Preenchida; o resto da receita é igual. */
  escolhida: boolean;
  /** Com `asChild`, a opção veste o filho (um `<Link>`) e marca `aria-current`. */
  asChild?: boolean;
}) {
  const Comp = asChild ? Slot : "button";
  // Quem a opção diz ser decide como a escolha é dita: a aba, `aria-selected`;
  // o link, `aria-current`; o botão de estado, `aria-pressed`.
  const marca =
    props.role === "tab"
      ? { "aria-selected": escolhida }
      : asChild
        ? { "aria-current": escolhida ? ("page" as const) : undefined }
        : { "aria-pressed": escolhida };
  return (
    <Comp
      data-slot="opcao-do-alternador"
      {...(asChild ? {} : { type: type ?? "button" })}
      {...marca}
      className={cn(opcaoDoAlternadorClass, className)}
      {...props}
    />
  );
}
